import React, { useState, useEffect } from 'react';
import { useUser } from '../UserContext';
import { useParams } from 'react-router-dom';
import PrizeNavigation from '../components/PrizeNavigation';
import Button from '../components/Button';
import EditForm from '../components/EditForm';
import Modal from '../components/Modal';
import PrizesTable from "../components/PrizesTable";
import styles from './PrizePage.module.css';
import EmptyState from '../components/EmptyState';
import useUserRole from '../hooks/useUserRole';
import { useLeague } from '../utils/LeagueContext';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import NoLeagueMessage from '../components/NoLeagueMessage';
import useLeagueAccessGuard from '../hooks/useLeagueAccessGuard';
import API_BASE_URL from '../utils/apiConfig'; // Import API base URL

const PrizePage = () => {
  const { user } = useUser();
  const [showForm, setShowForm] = useState(false);
  const [selectedPrizeType, setSelectedPrizeType] = useState(null); // Default prize type is null
  const [prizes, setPrizes] = useState({ eos: [], weekly: [], other: [] });
  const [selectedPrize, setSelectedPrize] = useState(null); // Track the prize being edited
  const [visibility, setVisibility] = useState({
    eos: true,
    weekly: true,
    other: true,
  }); // Track visibility of each table

  const { selectedLeague, selectedSeason, setSelectedSeason } = useLeague();
  const { leagueId: leagueIdFromURL } = useParams();
  const leagueId = selectedLeague ? selectedLeague._id : leagueIdFromURL || null;
  const userRole = useUserRole(selectedLeague);
  const [formKey, setFormKey] = useState(0);
  const noLeagueSelected = !selectedLeague || !selectedLeague._id;
  const currentYear = new Date().getFullYear();
  const [availableSeasons, setAvailableSeasons] = useState([currentYear]);
  useLeagueAccessGuard(); // Automatically handles auth, sets league, validates membership
  
  useEffect(() => {
    
    if (!leagueId) {
      return;
    }
  
    const fetchPrizes = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/prizes/${leagueId}`, {
          headers: { 'Authorization': `Bearer ${user.token}` },
        });
        const data = await response.json();

        // Derive available seasons from prize data
        const seasons = [...new Set(
          data.map(p => p.season ?? currentYear)
        )].sort((a, b) => b - a);
        setAvailableSeasons(seasons.length > 0 ? seasons : [currentYear]);

        // Filter by selected season
        const filtered = data.filter(p => (p.season ?? currentYear) === selectedSeason);
        setPrizes({
          eos: filtered.filter(prize => prize.type === 'eos'),
          weekly: filtered.filter(prize => prize.type === 'weekly'),
          other: filtered.filter(prize => prize.type === 'other'),
        });
      } catch (error) {
        console.error('Error fetching prizes:', error);
      }
    };
    fetchPrizes();
  }, [leagueId, user.token, selectedSeason]);

  const prizeFields = {
    eos: [
      { name: 'name', label: 'Prize name', type: 'text', placeholder: 'Enter Prize Name', required: true },
      { name: 'description', label: 'Prize description', type: 'text', placeholder: 'Enter Prize Description', required: true },
      { name: 'amount', label: 'Prize amount', type: 'number', placeholder: 'Enter Prize Amount', required: true },
      { name: 'winner', label: 'Winner', type: 'text', placeholder: 'Enter Winner', required: false },
    ],
    weekly: [
      { name: 'name', label: 'Prize name', type: 'text', placeholder: 'Enter Prize Name', required: true },
      { name: 'description', label: 'Prize description', type: 'text', placeholder: 'Enter Prize Description', required: true },
      { name: 'amount', label: 'Prize amount', type: 'number', placeholder: 'Enter Prize Amount', required: true },
      { name: 'winner', label: 'Winner', type: 'text', placeholder: 'Enter Winner', required: false },
    ],
    other: [
      { name: 'name', label: 'Prize name', type: 'text', placeholder: 'Enter Prize Name', required: true },
      { name: 'description', label: 'Prize description', type: 'text', placeholder: 'Enter Prize Description', required: true },
      { name: 'amount', label: 'Prize amount', type: 'number', placeholder: 'Enter Prize Amount', required: true },
      { name: 'winner', label: 'Winner', type: 'text', placeholder: 'Enter Winner', required: false },
    ]
  };

  // Columns Definitions
  const EosPrizesColumns = [
  userRole === 'commissioner' && {
    name: 'actions',
    label: 'Actions',
    render: (prize) => (
      <IconButton
        aria-label="Edit Prize"
        size="small"
        className={styles.editIcon}
        onClick={() => handleEditPrize(prize, 'eos')}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    ),
  },
    { name: 'name', label: 'Prize name' },
    { name: 'description', label: 'Prize description' },
    { name: 'amount', label: 'Prize amount' },
    { name: 'winner', label: 'Winner' },
  ];

  const WeeklyPrizesColumns = [
    userRole === 'commissioner' && {
      name: 'actions',
      label: 'Actions',
      render: (prize) => (
        <IconButton
          aria-label="Edit Prize"
          size="small"
          className={styles.editIcon}
          onClick={() => handleEditPrize(prize, 'weekly')}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    },
    { name: 'name', label: 'Prize name' },
    { name: 'description', label: 'Prize description' },
    { name: 'amount', label: 'Prize amount' },
    { name: 'winner', label: 'Winner' },
  ];

  const OtherPrizesColumns = [    
    userRole === 'commissioner' && {
      name: 'actions',
      label: 'Actions',
      render: (prize) => (
        <IconButton
          aria-label="Edit Prize"
          size="small"
          className={styles.editIcon}
          onClick={() => handleEditPrize(prize, 'other')}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    },
    { name: 'name', label: 'Prize name' },
    { name: 'description', label: 'Prize description' },
    { name: 'amount', label: 'Prize amount' },
    { name: 'winner', label: 'Winner' },
  ];

  const handleFormSubmit = async (formData) => {
    try {
      const dataToSubmit = { ...formData, type: selectedPrizeType, league: selectedLeague._id };

      let response;
      let updatedPrize;

      if (selectedPrize) {
        // Editing an existing prize
        response = await fetch(`${API_BASE_URL}/api/prizes/${selectedPrize._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user.token}`,
          },
          body: JSON.stringify(dataToSubmit),
        });
        updatedPrize = await response.json();
        if (!response.ok) {
          throw new Error('Error updating prize');
        }

        // Update the state with the edited prize
        setPrizes(prevState => ({
          ...prevState,
          [selectedPrizeType]: prevState[selectedPrizeType].map(prize =>
            prize._id === updatedPrize._id ? updatedPrize : prize
          ),
        }));
      } else {
        // Adding a new prize
        response = await fetch(`${API_BASE_URL}/api/prizes`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user.token}`,
          },
          body: JSON.stringify(dataToSubmit),
        });
        updatedPrize = await response.json();
        if (!response.ok) {
          throw new Error('Error adding prize');
        }

        // Update the state with the new prize
        setPrizes(prevState => ({
          ...prevState,
          [selectedPrizeType]: [...(prevState[selectedPrizeType] || []), updatedPrize],
        }));
      }

      // Close the form after successful submission
      setShowForm(false);
      setSelectedPrizeType(null);
      setSelectedPrize(null); // Reset the selected prize
    } catch (error) {
      console.error('Error submitting prize:', error);
      alert('Error submitting prize');
    }
  };

  const handleEditPrize = (prize, type) => {
    setSelectedPrize(prize); // Set the prize to be edited
    setSelectedPrizeType(type); // Set the prize type
    setShowForm(true); // Show the form modal
  };

  const handleDeletePrize = async () => {
    if (!selectedPrize) {
      console.error('No prize selected for deletion.');
      return;
    }
  
    const confirmDelete = window.confirm(
      `Are you sure you want to delete the prize "${selectedPrize.name}"?`
    );
    if (!confirmDelete) return;
  
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/prizes/${selectedPrize._id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${user.token}`,
          },
        }
      );
  
      if (!response.ok) {
        throw new Error('Error deleting prize');
      }
  
      // Remove the deleted prize from the correct table in the state
      setPrizes((prevState) => {
        const updatedPrizes = {
          ...prevState,
          [selectedPrizeType]: prevState[selectedPrizeType].filter(
            (prize) => prize._id !== selectedPrize._id
          ),
        };
  
        return updatedPrizes;
      });
  
      // Close the modal after deletion
      setShowForm(false);
      setSelectedPrize(null);
      setSelectedPrizeType(null);
    } catch (error) {
      console.error('Error deleting prize:', error);
      alert('Error deleting prize');
    }
  };

  const closeModal = () => {
    setShowForm(false);
    setSelectedPrize(null); // Reset the selected prize
    setSelectedPrizeType(null); // Reset the prize type
    setFormKey(prev => prev + 1); // Force EditForm to reset
  };

  const renderTableOrEmpty = (data, type) => {
    if (!data || data.length === 0) {
      const messages = {
        eos: "No end of season prizes added yet. Click 'Add EOS prize' to get started.",
        weekly: "No weekly prizes added yet. Click 'Add Weekly prize' to get started.",
        other: "No other prizes added yet. Click 'Add Other prize' to get started."
      };
      return <EmptyState message={messages[type]} icon="🏆" />;
    }

    // Set columns based on prize type
    let columns;
    switch (type) {
      case 'eos':
        columns = EosPrizesColumns;
        break;
      case 'weekly':
        columns = WeeklyPrizesColumns;
        break;
      case 'other':
        columns = OtherPrizesColumns;
        break;
      default:
        columns = [];
    }

    return <PrizesTable data={data} columns={columns} />;
  };

  // Toggle visibility of prize type tables
  const toggleVisibility = (type) => {
    setVisibility(prevVisibility => ({
      ...prevVisibility,
      [type]: !prevVisibility[type],
    }));
  };

  return (
    <>
      <div>
        <PrizeNavigation navigationButtonArea="/2813821-1@2x.png" leagueId={leagueId} />
      </div>
      <div className={styles.prizesHeader}>
      {!selectedLeague && (
          <NoLeagueMessage entity="prizes" className={styles.leagueName} />
        )}
        <p className={styles.leagueName}>
          {selectedLeague ? selectedLeague.leagueName : ""}
        </p>
        <h3 className={styles.prizesTitle}>Prizes
          <span className={styles.titleIcon}>🏆</span>
        </h3>

        {selectedLeague && availableSeasons.length > 1 && (
          <select
            className={styles.seasonSelector}
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(Number(e.target.value))}
          >
            {availableSeasons.map(season => (
              <option key={season} value={season}>{season} Season</option>
            ))}
          </select>
        )}
      </div>

      {selectedLeague && (
        <div className={styles.prizeDetails}>
          {/* Button always says "Add prize" */}
          {userRole === 'commissioner' && (
            <Button
              label="Add prize"
              onClick={() => setShowForm(true)} // Show the form when the button is clicked
            />
          )}

          {/* Render a dropdown to allow the user to select the prize type */}
          {showForm && (
            <Modal onClose={closeModal}>
              <div>
                <select
                  className={styles.select}
                  onChange={(e) => setSelectedPrizeType(e.target.value)}
                  value={selectedPrizeType || ''}
                  disabled={!!selectedPrize} // Disable changing type when editing
                >
                  <option value="">Select Prize Type</option>
                  <option value="eos">End of Season</option>
                  <option value="weekly">Weekly</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {selectedPrizeType && (
                <EditForm
                  fields={prizeFields[selectedPrizeType]}
                  onSubmit={handleFormSubmit}
                  initialValues={selectedPrize || {}} // Pre-fill form when editing
                  isEditing={!!selectedPrize} // Pass editing state
                  key={formKey} // Use formKey to force re-render
                />
              )}

              {/* Show the Delete button only when editing an existing prize */}
              {selectedPrize && (
                <div
                  className={styles.deletePrize}
                  onClick={handleDeletePrize}
                >
                  Delete
                </div>
              )}
            </Modal>
          )}

          {/* Render the dtables */}
          {['eos', 'weekly', 'other'].map((type) => (
            <div key={type}>
              <h4 className={styles.prizeSection}>
                {type === 'eos' ? 'End of Season' : type.charAt(0).toUpperCase() + type.slice(1)} Prizes
                <br></br>
                <span
                  className={styles.toggleText}
                  onClick={() => toggleVisibility(type)}
                  style={{ cursor: 'pointer' }}
                >
                  {visibility[type] ? 'Hide' : 'Show'} Table
                </span> 
              </h4>

              {visibility[type] && renderTableOrEmpty(prizes[type], type)}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default PrizePage;
